'use client';

import React, { useState } from 'react';
import { Mail, FileText, Search, Sparkles, Lightbulb } from 'lucide-react';

const SUBSCRIBE_URL = 'https://app.superpowerresume.com/api/newsletter/subscribe';

export default function NewsletterContent() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    try {
      const res = await fetch(SUBSCRIBE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = (await res.json()) as { success?: boolean; message?: string; error?: string };
      if (res.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Subscribed! Check your inbox.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  return (
    <section className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center mt-0">
      <div className="bg-white/90 rounded-2xl shadow-2xl px-8 py-16 w-full max-w-3xl flex flex-col items-center">
        <div className="mb-6 flex items-center justify-center">
          <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg border-4 border-white">
            <Mail className="w-9 h-9" />
          </span>
        </div>
        <h1 className="text-4xl font-extrabold mb-4 text-center text-gray-900 tracking-tight">
          Level Up Your Job Search
        </h1>
        <p className="text-lg md:text-xl text-gray-700 mb-10 max-w-2xl mx-auto text-center">
          Join thousands of professionals getting weekly resume tips, job search
          strategies, and AI career advice — straight to your inbox.
        </p>

        {/* What You'll Get */}
        <div className="w-full max-w-xl mb-10">
          <div className="bg-white/90 rounded-xl shadow p-6 border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-center text-gray-900">
              What You&apos;ll Get
            </h2>
            <ul className="space-y-3 text-base md:text-lg text-gray-700">
              {[
                { icon: FileText, label: 'Resume Tips:', desc: 'Proven strategies to beat ATS systems and land more interviews.' },
                { icon: Search, label: 'Job Search Playbooks:', desc: 'Step-by-step guides for every stage of your search.' },
                { icon: Sparkles, label: 'AI Career Tools:', desc: 'How to use AI to work smarter, not harder on applications.' },
                { icon: Lightbulb, label: 'New Blog Posts:', desc: 'Be the first to read our latest career guides and advice.' },
              ].map(({ icon: Icon, label, desc }) => (
                <li key={label} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span>
                    <b>{label}</b> {desc}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Form */}
        <div className="w-full">
          <div className="bg-white/90 rounded-2xl p-6 md:p-10 shadow-xl border border-gray-100 w-full max-w-full md:max-w-xl mx-auto">
            {status === 'success' ? (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-semibold text-gray-900">{message}</p>
                <p className="text-sm text-gray-500 mt-1">We just sent you a welcome email with some great articles to get started.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <label htmlFor="newsletter-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="flex gap-3">
                  <input
                    id="newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                    placeholder="you@example.com"
                    required
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="rounded-xl bg-blue-600 px-6 py-3 text-base font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {status === 'loading' ? 'Joining...' : 'Subscribe'}
                  </button>
                </div>
                {status === 'error' && (
                  <p className="text-sm text-red-500 mt-2">{message}</p>
                )}
              </form>
            )}
            <p className="text-sm text-gray-400 mt-4 text-center">
              No spam, ever. We only send content that helps your career.
              Unsubscribe anytime.
            </p>
          </div>
        </div>

        {/* CTA to product */}
        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-4">
            Ready to build your resume right now?
          </p>
          <a
            href="https://app.superpowerresume.com/sign-up"
            className="inline-block bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow text-lg border border-transparent text-center transition-colors duration-200"
          >
            Try Superpower Resume Free
          </a>
        </div>
      </div>
    </section>
  );
}
